const EventEmitter = require('eventemitter3');
const { v4: uuidv4 } = require('uuid');
const getBattleStateHelperFuncs = require('./getBattleStateHelperFuncs');
const StatRecovery = require('./StatRecovery');
const BattleCalc = require('./BattleCalc');
const DeathEvent = require('./events/DeathEvent');
const SummonedCondition = require('./conditions/SummonedCondition');
const { logError } = require('./logger');

const BattleState = function (state, isRecovery, isCaverns) {
  this.isCaverns = isCaverns;

  this.allTiles = state.allTiles;
  this.allUnits = state.allUnits;
  this.allPieces = { ...state.allTiles, ...state.allUnits };
  this.black_team = state.black_team;
  this.white_team = state.white_team;

  this.doors = state.doors || [];
  this.portal = state.portal;
  this.walls = state.walls || [];

  let done = false;
  this.eventEmitter = new EventEmitter();

  // this awful garbage is needed for external calls to be made like battleState.getAdjacentTiles(unit) AND internal calls like this.getAdjacentTiles(unit)
  const hf = getBattleStateHelperFuncs(this);
  for (const [key, fn] of Object.entries(hf)) {
    this[key] = fn;
  }

  // (for now) we have to declare a bunch of functions first, then call this at the end of the BattleState() func
  this.init = () => {
    // initialize tile occupied states
    for (const tile of Object.values(this.allTiles)) {
      tile.occupied = false;

      if (tile.x === this.portal?.x && tile.y === this.portal?.y) {
        tile.occupied = true;
      } else {
        if (!Array.isArray(this.doors) && typeof this.doors === 'object') {
          this.doors = Object.values(this.doors);
        }
        for (const door of this.doors || []) {
          if (tile.x === door.x && tile.y === door.y) {
            tile.occupied = true;
            break;
          }
        }
      }
    }

    // initialize units
    for (const unit of Object.values(this.allUnits)) {
      this.registerNewUnit(unit);

      // in a new instance we need to restart the summoned units' destruction timers (since conditions are cleared)
      if (['dwarf', 'faerie', 'elf'].includes(unit.handle)) {
        this.onUnitCondition(
          new SummonedCondition(
            unit,
            unit,
            this,
            30 * 1000 // arbitrary!
          )
        );
      }
    }
  };

  this.registerNewUnit = (unit) => {
    try {
      if (done || !unit) {
        return;
      }

      if (!unit.uid) {
        throw new Error('unit has no uid');
      }
      this.allPieces[unit.uid] = unit;
      this.allUnits[unit.uid] = unit;

      var tileUnderUnit = this.getTileAtCoords(unit.x, unit.y);
      tileUnderUnit.occupied = true;

      // while we're at it, initialize some values on the unit
      unit.inPlay = true;
      unit.dead = false;
      if (!isRecovery) {
        unit.hp = unit.max_hp;
        unit.ap = 0;
        unit.mp = 0;

        unit.conditions = {};
      }

      if (unit.tutorial_01_enemy) {
        unit.max_hp = 1;
        unit.hp = 1;
        unit.max_ap = 10000;
        unit.max_mp = 10000;
      } else if (unit.tutorialHero) {
        unit.mp = unit.max_mp;
      }

      this.onBattleEvent({
        eventHandle: 'unit_spawned',
        unit,
        affectedPieces: [unit, tileUnderUnit],
        movement: true,
      });
    } catch (err) {
      logError(err, {
        module: 'BattleState',
        func: 'registerNewUnit',
        unit,
      });
    }
  };

  this.paused = isCaverns ? true : state ? state.paused || false : false;
  this.pause = () => {
    try {
      this.paused = true;

      if (_statRecovery) {
        _statRecovery.pause();
      }

      // pause condition timers
      for (const c of Object.values(_conditions)) {
        if (c.intervalTimer) {
          c.intervalTimer.pause();
        }
      }
    } catch (err) {
      logError(err, {
        module: 'BattleState',
        func: 'pause',
      });
    }
  };
  this.resume = () => {
    try {
      if (done) {
        return;
      }

      this.paused = false;

      if (_statRecovery) {
        _statRecovery.resume();
      }

      // resume condition timers
      for (const c of Object.values(_conditions)) {
        if (c.intervalTimer) {
          c.intervalTimer.resume();
        }
      }
    } catch (err) {
      logError(err, {
        module: 'BattleState',
        func: 'resume',
      });
    }
  };

  let _statRecovery = StatRecovery({
    allUnits: this.allUnits,
    initPaused: isCaverns || this.paused,
    unitAPFullCallback: (unit) => {
      this.eventEmitter.emit(BattleState.AP_FULL, unit);
    },
    logger: { logError },
  });

  let _conditions = {};
  this.getConditions = () => {
    return _conditions;
  };
  this.conditionId = state ? state.conditionId || 0 : 0;

  this.onUnitCondition = (condition, options) => {
    if (done) {
      return;
    }

    try {
      const owner = this.allPieces[condition.ownerId] || condition.owner;
      if (!owner || !owner.inPlay) {
        condition?.expire();
        return;
      }

      condition.uid = uuidv4();

      // add the condition
      _conditions[condition.uid] = condition;

      // initialize a conditions object on the owner if none exists
      if (!owner.conditions) {
        owner.conditions = {};
      }

      var startTime = condition.startTime || Date.now();

      // add a state object to the owner
      const cState = (owner.conditions[condition.uid] = {
        uid: condition.uid,
        instigatorId: condition.instigatorId,
        ownerId: owner.uid,
        handle: condition.handle,
        startTime,
        duration: condition.duration,
        potency: condition.potency,
        charges: condition.charges,
        options,
      });

      this.allPieces[owner.uid] = owner;
      this.allUnits[owner.uid] = owner;

      this.eventEmitter.emit(BattleState.UNIT_CONDITION, {
        owner,
        cState,
        options,
      });
    } catch (err) {
      logError(err, {
        module: 'BattleState',
        func: 'onUnitCondition',
        condition,
      });
    }
  };

  this.updateCondition = (unitId, conditionId) => {
    if (done || !_conditions[conditionId]) {
      return;
    }

    try {
      const unit = this.allUnits[unitId];
      const cState = unit.conditions[conditionId];

      _conditions[conditionId].potency = cState.potency;
      _conditions[conditionId].charges = cState.charges;

      this.eventEmitter.emit(BattleState.UNIT_CONDITION, {
        owner: unit,
        cState,
      });
    } catch (err) {
      logError(err, {
        module: 'BattleState',
        func: 'updateCondition',
        unitId,
        conditionId,
      });
    }
  };

  this.expireCondition = (conditionId, options) => {
    try {
      if (done) {
        return;
      }

      const c = _conditions[conditionId];
      if (!c) {
        return;
      }

      const owner = this.allPieces[c.ownerId];
      if (owner) {
        delete owner.conditions[conditionId];

        this.eventEmitter.emit(BattleState.UNIT_CONDITION_EXPIRED, {
          owner,
          ownerId: c.ownerId,
          conditionId: conditionId,
          conditionHandle: c.handle,
          options,
        });

        if (c.handle === 'summoned') {
          new DeathEvent(this).execute(owner);
        }
      }

      // c.expire();
      c.dispose();
      delete _conditions[conditionId];
    } catch (err) {
      logError(err, {
        module: 'BattleState',
        func: 'expireCondition',
        conditionId,
      });
    }
  };

  this.onBattleEvent = (event) => {
    try {
      if (done) {
        return;
      }

      if (event.eventHandle === 'death') {
        for (const cid of Object.keys(_conditions || {})) {
          if (_conditions[cid].ownerId === event.victimId) {
            this.expireCondition(cid);
          }
        }

        delete this.allPieces[event.victimId];
        delete this.allUnits[event.victimId];

        var numWhite = 0;
        var numBlack = 0;
        for (const unit of Object.values(this.allUnits || {}).filter(
          ({ hero, poppet }) => hero && !poppet
        )) {
          if (unit.team === 'white') {
            ++numWhite;
          } else if (unit.team === 'black') {
            ++numBlack;
          }
        }

        if (numWhite === 0) {
          this.eventEmitter.emit(BattleState.BATTLE_RESOLVED, {
            winner: 'black',
          });
        } else if (numBlack === 0) {
          this.eventEmitter.emit(BattleState.BATTLE_RESOLVED, {
            winner: 'white',
          });
        }
      }

      this.eventEmitter.emit(BattleState.BATTLE_EVENT, event);
    } catch (err) {
      logError(err, {
        module: 'BattleState',
        func: 'onBattleEvent',
        event,
      });
    }
  };

  const clearGamePieceDictionaries = () => {
    const all_dicts = [this.allTiles, this.allUnits, this.allPieces];
    all_dicts.forEach((dict) => {
      for (const key of Object.keys(dict)) {
        delete dict[key];
      }
    });
  };

  this.onCavernsTravel = ({ new_room: { tiles, doors, walls, portal }, all_units_array }) => {
    // we'll rebuild all the game piece dictionaries
    clearGamePieceDictionaries();

    this.doors = doors;
    this.walls = walls;
    this.portal = portal;

    // update tiles
    for (const tile of tiles) {
      if (!tile.uid) {
        throw new Error('tile has no uid');
      }

      this.allTiles[tile.uid] = tile;
      this.allPieces[tile.uid] = tile;

      tile.occupied = false;
      if (tile.x === portal?.x && tile.y === portal?.y) {
        tile.occupied = true;
      } else {
        for (const unit of all_units_array) {
          if (tile.x === unit.x && tile.y === unit.y) {
            tile.occupied = true;
          }
        }
        for (const door of doors) {
          if (tile.x === door.x && tile.y === door.y) {
            tile.occupied = true;
            break;
          }
        }
      }
    }

    for (const unit of all_units_array) {
      this.allUnits[unit.uid] = unit;
      this.allPieces[unit.uid] = unit;
    }
  };

  this.dispose = function () {
    try {
      done = true;

      for (const [condition_id, condition] of Object.entries(_conditions)) {
        this.expireCondition(condition_id);
        delete _conditions[condition_id];
      }
      _conditions = null;

      if (this.eventEmitter) {
        this.eventEmitter.removeAllListeners();
        this.eventEmitter = null;
      }

      clearGamePieceDictionaries();
      this.doors = null;
      this.walls = null;
      this.allPieces = null;
      this.allUnits = null;
      this.allTiles = null;

      if (_statRecovery) {
        _statRecovery.dispose();
        _statRecovery = null;
      }
    } catch (err) {
      logError(err, {
        module: 'BattleState',
        func: 'dispose',
      });
    }
  };
};
module.exports = BattleState;
module.exports.AP_FULL = 'AP_FULL';
module.exports.MP_FULL = 'MP_FULL';
module.exports.BATTLE_EVENT = 'BATTLE_EVENT';
module.exports.UNIT_CONDITION = 'UNIT_CONDITION';
module.exports.UNIT_CONDITION_EXPIRED = 'UNIT_CONDITION_EXPIRED';
module.exports.BATTLE_RESOLVED = 'BATTLE_RESOLVED';
